import React, { useEffect, useMemo } from 'react';
import Table from "src/componets/Table";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { UserNumberInterface } from "src/store/callrecorder/userNumbersSlice";
import { fetchUserNumbers } from "src/actions/callrecorder/userNumbersActions";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";

import { Badge, Tag, Tooltip } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";

interface ListTableInterface {
  filterParams: object;
  limit?: number;
}

const _ListTable = ({ filterParams, limit = 25 }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const ids = useAppSelector(state => state.callrecorder.userNumbers.ids);
  const entities = useAppSelector(state => state.callrecorder.userNumbers.entities);
  const count = useAppSelector(state => state.callrecorder.userNumbers.count);
  const data = ids ? ids.map(id => entities[id]) : [];

  const params = useMemo(() => ({
    limit,
    ...filterParams
  }), [limit, filterParams]);

  useEffect(() => {
    dispatch(fetchUserNumbers(params));
  }, [params, dispatch]);

  const columns: ColumnsType<UserNumberInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id, url }) => <OptionalLink link={url}><small>{`#${id}`}</small></OptionalLink>,
      width: 60
    },

    {
      key: 'number',
      title: 'Number',
      render: (_, { number, active }) => (
        <span>
          <small>{number}</small>
          {active && (
            <Tooltip title="Current used number">
              <CheckCircleFilled style={{ color: '#52c41a', marginLeft: 6, fontSize: 12 }} />
            </Tooltip>
          )}
        </span>
      ),
      width: 150
    },

    {
      key: 'friendly_name',
      title: 'Friendly Name',
      render: (_, { friendly_name }) => friendly_name || '—',
      width: 120
    },

    {
      key: 'number_type',
      title: 'Type',
      render: (_, { number_type }) => {
        const color = number_type === 'purchased' ? 'blue' : number_type === 'virtual' ? 'purple' : 'default';
        return <Tag color={color}>{number_type}</Tag>;
      },
      width: 90
    },

    {
      key: 'sms_verified',
      title: 'SMS Verified',
      render: (_, { sms_verified }) => sms_verified ? <Tag color="success">yes</Tag> : <Tag color="default">no</Tag>,
      width: 55
    },

    {
      key: 'call_verified',
      title: 'Call Verified',
      render: (_, { call_verified }) => call_verified ? <Tag color="success">yes</Tag> : <Tag color="default">no</Tag>,
      width: 55
    },

    {
      key: 'subscription_status',
      title: 'Subscription',
      render: (_, { subscription_status }) => {
        if (!subscription_status) return '—';
        const color = subscription_status === 'active' ? 'success'
          : subscription_status === 'pending' ? 'processing'
          : subscription_status === 'cancelled' ? 'warning'
          : subscription_status === 'expired' ? 'error'
          : 'default';
        return <Tag color={color}>{subscription_status}</Tag>;
      },
      width: 100
    },

    {
      key: 'incoming_sms',
      title: 'In SMS',
      render: (_, { incoming_sms_count, total_incoming_sms_limit, extra_incoming_sms_count }) => {

        const limit = extra_incoming_sms_count > 0
          ? (
              <Tooltip title="Extra SMS purchased">
                <Badge
                  style={{ backgroundColor: '#6610f2', padding: '0 7px', fontSize: '10px' }}
                  count={`+${extra_incoming_sms_count}`}
                  size="small"
                  offset={[11, -3]}
                >
                  <small>{total_incoming_sms_limit}</small>
                </Badge>
              </Tooltip>
            )
          : <span>{total_incoming_sms_limit}</span>;

        return <small>{incoming_sms_count}/{limit}</small>;
      },
      width: 100
    },

    {
      key: 'unread_sms_count',
      title: 'Unread SMS',
      render: (_, { unread_sms_count }) => <small>{unread_sms_count}</small>,
      width: 80
    },

    {
      key: 'created_at',
      title: 'Created',
      render: (_, { created_at }) => <small>{formattedDate(created_at).dateTime}</small>,
      width: 125
    },
  ];

  return (
    <div>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
